import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import ImportExportBar from "./ImportExportBar";
import StateSelect from "./StateSelect";
import { GST_STATES, gstStateByCode, gstStateName } from "./gstStates";
import Pagination from "./Pagination";
import { CustomerScopeFilters, beatsOfSalesman } from "./CustomerSelect";
import { useReceivableLookups } from "./receivables/common";

const money = (value) =>
  value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Every detail of a customer, with purchase / payment figures and the account balance. */
const CustomerView = ({ customer, insight, beat, onClose, onEdit }) => {
  const [account, setAccount] = useState(null);
  useEffect(() => {
    api.get(`/api/v1/customers/${customer.id}/account`).then(setAccount).catch(() => setAccount(null));
  }, [customer.id]);
  const Item = ({ label, children }) => (
    <div>
      <div className="page-subtitle">{label}</div>
      <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{children === null || children === undefined || children === "" ? "-" : children}</div>
    </div>
  );
  const extra = Object.entries(customer.customAttributes || {}).filter(([, v]) => v !== null && v !== "");
  return (
    <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 900 }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">
            {customer.retailerCode ? `${customer.retailerCode} - ` : ""}
            {customer.name}
          </h3>
          <div className="flex gap-2">
            <button className="secondary" onClick={onEdit}>
              Edit
            </button>
            <button className="close-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px 20px" }}>
          <Item label="Customer ID">{customer.id}</Item>
          <Item label="Customer Code">{customer.retailerCode}</Item>
          <Item label="Name">{customer.name}</Item>
          <Item label="Phone">{customer.phone}</Item>
          <Item label="GSTIN">{customer.gstin}</Item>
          <Item label="Address">{customer.address}</Item>
          <Item label="City">{customer.city}</Item>
          <Item label="Pincode">{customer.pincode}</Item>
          <Item label="State">{customer.stateCode ? `${gstStateName(customer.stateCode) || ""} (${customer.stateCode})` : null}</Item>
          <Item label="Beat">{beat?.beatName || (customer.beatId ? `#${customer.beatId}` : null)}</Item>
          <Item label="Salesman">{beat?.salesmanName}</Item>
          <Item label="Credit Limit">₹{money(customer.creditLimit)}</Item>
          <Item label="Credit Days">{customer.creditDays}</Item>
          <Item label="Outstanding">₹{money(customer.outstandingBalance)}</Item>
          <Item label="Account Balance (credit notes)">{account ? `₹${money(account.balance)}` : null}</Item>
          <Item label="Status">{customer.active ? "Active" : "Inactive"}</Item>
          {extra.map(([key, value]) => (
            <Item key={key} label={key}>
              {String(value)}
            </Item>
          ))}
        </div>
        <h4 style={{ margin: "18px 0 8px", fontWeight: 600 }}>Purchases and payments</h4>
        {insight ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px 20px" }}>
            <Item label="Bills">{insight.bills}</Item>
            <Item label="Total Purchase">₹{money(insight.totalPurchase)}</Item>
            <Item label="Avg Monthly Purchase">₹{money(insight.averageMonthlyPurchase)}</Item>
            <Item label="Avg Purchase (per bill) Till Date">₹{money(insight.averagePurchaseTillDate)}</Item>
            <Item label="Payments">{insight.payments}</Item>
            <Item label="Total Payment">₹{money(insight.totalPayment)}</Item>
            <Item label="Avg Monthly Payment">₹{money(insight.averageMonthlyPayment)}</Item>
            <Item label="Avg Payment Till Date">₹{money(insight.averagePaymentTillDate)}</Item>
            <Item label="First Purchase">{insight.firstPurchaseDate}</Item>
            <Item label="Last Purchase">{insight.lastPurchaseDate}</Item>
            <Item label="Months Active">{insight.monthsActive}</Item>
          </div>
        ) : (
          <div className="page-subtitle">No purchases or payments yet.</div>
        )}
      </div>
    </div>
  );
};

const PAGE_SIZE = 50;

const emptyForm = {
  name: "",
  phone: "",
  address: "",
  gstin: "",
  creditLimit: "",
  creditDays: "",
  active: true,
  stateCode: "",
  city: "",
  pincode: "",
  retailerCode: "",
  beatId: "",
  visitDay: "",
};

const VISIT_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  // salesman / beats narrowing the list, purchase / payment figures of the listed customers, the customer being viewed
  const lookups = useReceivableLookups(["salesmen", "beats"]);
  const [scope, setScope] = useState({ salesmanUserId: "", beatIds: [] });
  const [insights, setInsights] = useState({});
  const [viewing, setViewing] = useState(null);

  const scopeBeatIds = () => {
    if (scope.beatIds.length) return scope.beatIds;
    if (scope.salesmanUserId) {
      const ids = beatsOfSalesman(lookups.beats, scope.salesmanUserId).map((b) => b.id);
      return ids.length ? ids : [-1];
    }
    return [];
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/customers", {
        page,
        size: PAGE_SIZE,
        name: nameFilter.trim() || undefined,
        phone: phoneFilter.trim() || undefined,
        beatIds: scopeBeatIds().length ? scopeBeatIds().join(",") : undefined,
      });
      const list = data?.content || [];
      setCustomers(list);
      if (list.length) {
        api
          .get("/api/v1/customers/insights", { customerIds: list.map((c) => c.id).join(",") })
          .then((rows) => setInsights(Object.fromEntries((rows || []).map((row) => [row.customerId, row]))))
          .catch(() => setInsights({}));
      } else setInsights({});
      setTotalPages(data?.totalPages ?? 0);
      setTotalElements(data?.totalElements ?? 0);
    } catch (error) {
      toast.error(error.message || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  // Changing a filter should always jump back to page 0 — otherwise a narrower result set can land you past
  // its last page, silently showing nothing.
  useEffect(() => {
    setPage(0);
  }, [nameFilter, phoneFilter, scope]);

  useEffect(() => {
    // Debounce the free-text filters so we don't fire a request on every keystroke.
    const timeout = setTimeout(loadCustomers, nameFilter || phoneFilter ? 400 : 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, nameFilter, phoneFilter, scope, lookups.beats]);

  useEffect(() => {
    api
      .get("/api/v1/beats", { size: 1000 })
      .then((data) => setBeats(data?.content || []))
      .catch(() => {});
  }, []);

  const beatName = (beatId) => beats.find((beat) => beat.id === beatId)?.beatName;
  const beatOf = (beatId) => {
    const beat = beats.find((b) => b.id === beatId);
    const lookup = lookups.beats.find((b) => b.id === beatId);
    return beat ? { ...beat, salesmanName: lookup?.salesmanName } : null;
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (customer) => {
    setEditingId(customer.id);
    setFormData({
      name: customer.name || "",
      phone: customer.phone || "",
      address: customer.address || "",
      gstin: customer.gstin || "",
      creditLimit: customer.creditLimit ?? "",
      creditDays: customer.creditDays ?? "",
      active: customer.active ?? true,
      stateCode: customer.stateCode || "",
      city: customer.city || "",
      pincode: customer.pincode || "",
      retailerCode: customer.retailerCode || "",
      beatId: customer.beatId ?? "",
      // blank keeps the visit day the customer already has on that beat
      visitDay: "",
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // Every GSTIN's first 2 digits are that state's official GST code — pre-fill them so the user
  // only has to type the rest, replacing just the leading digits if some GSTIN was already typed.
  const handleStateSelect = (state) => {
    setFormData((prev) => ({ ...prev, gstin: state.gstCode + prev.gstin.slice(2) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        creditLimit: formData.creditLimit === "" ? 0 : Number(formData.creditLimit),
        creditDays: formData.creditDays === "" ? 0 : Number(formData.creditDays),
        retailerCode: formData.retailerCode.trim() || null,
        // 0 takes an existing customer off their beat; a new customer simply gets none
        beatId: formData.beatId === "" ? (editingId ? 0 : null) : Number(formData.beatId),
        visitDay: formData.beatId === "" ? null : formData.visitDay || null,
      };
      if (editingId) {
        await api.put(`/api/v1/customers/${editingId}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/api/v1/customers", payload);
        toast.success("Customer created");
      }
      setShowModal(false);
      loadCustomers();
    } catch (error) {
      toast.error(error.message || "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading customers...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Filter by code / name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          style={{ width: "160px" }}
        />
        <input
          type="text"
          placeholder="Filter by phone"
          value={phoneFilter}
          onChange={(e) => setPhoneFilter(e.target.value)}
          style={{ width: "150px" }}
        />
        <CustomerScopeFilters scope={scope} onChange={setScope} salesmen={lookups.salesmen} beats={lookups.beats} compact />
        {nameFilter || phoneFilter || scope.salesmanUserId || scope.beatIds.length ? (
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setNameFilter("");
              setPhoneFilter("");
              setScope({ salesmanUserId: "", beatIds: [] });
            }}
          >
            Clear filters
          </button>
        ) : null}
        <button className="primary" onClick={openCreate}>
          + New Customer
        </button>
        <ImportExportBar
          importPath="/api/v1/customers/import"
          exportPath="/api/v1/customers/export"
          exportFilename="customers.xlsx"
          label="Customers"
          onImported={loadCustomers}
        />
      </div>
      <div className="page-subtitle" style={{ margin: "4px 0 10px" }}>
        Excel import: a <b>Beat Name</b> column puts each customer on that beat (created when it does not exist yet); an optional
        Visit Day column (MON..SUN) sets the visit day, MON when blank.
      </div>

      <div className="table-wrapper">
        {/* fixed column widths: every column fits the page, long values wrap instead of scrolling sideways */}
        <table className="fit-table">
          <colgroup>
            <col style={{ width: "3.5%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "7.5%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "5.5%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "5.5%" }} />
            <col style={{ width: "8.5%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>ID</th>
              <th>Code</th>
              <th>Name</th>
              <th>Phone</th>
              <th>GSTIN</th>
              <th>State</th>
              <th>Beat</th>
              <th>Credit Limit</th>
              <th>Outstanding</th>
              <th title="Bills over the months since the first bill">Avg Mthly Purchase</th>
              <th title="Payments over the months since the first bill">Avg Mthly Payment</th>
              <th title="Average bill value till date">Avg Purchase (Till Date)</th>
              <th title="Average payment received till date">Avg Payment (Till Date)</th>
              <th>Last Purchase</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan={16}>No customers found.</td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.id}>
                  <td>{customer.id}</td>
                  <td>{customer.retailerCode || "-"}</td>
                  <td>{customer.name}</td>
                  <td>{customer.phone || "-"}</td>
                  <td>{customer.gstin || "-"}</td>
                  <td title={customer.stateCode ? `${gstStateName(customer.stateCode) || ""} (${customer.stateCode})` : ""}>
                    {customer.stateCode ? gstStateName(customer.stateCode) || customer.stateCode : "-"}
                  </td>
                  <td>{customer.beatId ? beatName(customer.beatId) || `#${customer.beatId}` : "-"}</td>
                  <td>{customer.creditLimit}</td>
                  <td>{customer.outstandingBalance}</td>
                  <td>{money(insights[customer.id]?.averageMonthlyPurchase)}</td>
                  <td>{money(insights[customer.id]?.averageMonthlyPayment)}</td>
                  <td>{money(insights[customer.id]?.averagePurchaseTillDate)}</td>
                  <td>{money(insights[customer.id]?.averagePaymentTillDate)}</td>
                  <td>{insights[customer.id]?.lastPurchaseDate || "-"}</td>
                  <td>
                    <span className={`badge ${customer.active ? "badge-green" : "badge-gray"}`}>
                      {customer.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => setViewing(customer)}>
                      View
                    </button>
                    <button className="secondary" onClick={() => openEdit(customer)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} />

      {viewing ? (
        <CustomerView
          customer={viewing}
          insight={insights[viewing.id]}
          beat={beatOf(viewing.beatId)}
          onClose={() => setViewing(null)}
          onEdit={() => {
            const customer = viewing;
            setViewing(null);
            openEdit(customer);
          }}
        />
      ) : null}

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Customer" : "Create Customer"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" value={formData.phone} onChange={handleChange} />
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Address</label>
                  <input name="address" value={formData.address} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>GSTIN</label>
                  <input name="gstin" value={formData.gstin} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  {/* picking a state fills the GST state code below; a hand-typed code selects its state here */}
                  <select
                    value={gstStateByCode(formData.stateCode)?.code || ""}
                    onChange={(e) => setFormData((prev) => ({ ...prev, stateCode: e.target.value }))}
                  >
                    <option value="">Select state</option>
                    {GST_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>State Code (GST, used for e-invoice / e-way bill)</label>
                  <input name="stateCode" value={formData.stateCode} onChange={handleChange} placeholder="e.g. 29" maxLength={10} />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input name="city" value={formData.city} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Pincode</label>
                  <input name="pincode" value={formData.pincode} onChange={handleChange} placeholder="e.g. 560001" />
                </div>
                <div className="form-group">
                  <label>Retailer Code (used by sales invoice import)</label>
                  <input name="retailerCode" value={formData.retailerCode} onChange={handleChange} maxLength={50} />
                </div>
                <div className="form-group">
                  <label>Beat</label>
                  <select name="beatId" value={formData.beatId} onChange={handleChange}>
                    <option value="">No beat</option>
                    {beats
                      .filter((beat) => beat.active || String(beat.id) === String(formData.beatId))
                      .map((beat) => (
                        <option key={beat.id} value={beat.id}>
                          {beat.beatName}
                          {beat.salesmanName ? ` (${beat.salesmanName})` : ""}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Visit Day on the Beat</label>
                  <select name="visitDay" value={formData.visitDay} onChange={handleChange} disabled={formData.beatId === ""}>
                    <option value="">Keep current (Monday for a new mapping)</option>
                    {VISIT_DAYS.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Credit Limit</label>
                  <input type="number" min="0" name="creditLimit" value={formData.creditLimit} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Credit Days</label>
                  <input type="number" min="0" name="creditDays" value={formData.creditDays} onChange={handleChange} placeholder="e.g. 30" />
                </div>
                <div className="form-group">
                  <label>Active</label>
                  <select
                    name="active"
                    value={formData.active ? "true" : "false"}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === "true" }))}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Customers;
